import {
  BooleanInput,
  Filter,
  FunctionField,
  sanitizeListRestProps,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { Pill } from 'shared/components/Pill/Pill'
import { Text } from 'shared/components/Text/Text'
import {
  EquipmentStatus,
  EquipmentStatusLabel,
  OwnershipType,
  OwnershipTypeLabel,
} from 'shared/models/equipment'
import { TrailerType, TrailerTypeLabel } from 'shared/models/trailer'
import { errorD1 } from 'shared/theme/colors'

import ErrorIconFilled from '../../assets/images/ErrorIconFilled.svg'
import { useHasEquipmentManagement } from '../../hooks/useHasFeatureFlag'
import { CTDatagrid } from '../CTDatagrid'
import CTList from '../List'

const VendorChoices = [
  { id: 'METRO', name: 'METRO' },
  { id: 'PENSKE', name: 'PENSKE' },
  { id: 'NORTH_AMERICAN', name: 'NORTH_AMERICAN' },
  { id: 'XTRA', name: 'XTRA' },
  { id: 'MILESTONE', name: 'MILESTONE' },
  { id: 'OTHER', name: 'OTHER' },
]

// TODO: @public-equipment-management Update the filters ui and fields when models are updated
const TrailersListFilter = (props: any) => (
  <Filter {...props}>
    <TextInput alwaysOn label="Search by VIN, location" source="search" />
    <SelectInput
      alwaysOn
      label="Ownership"
      source="ownership_type"
      choices={[
        { id: OwnershipType.OWN, name: OwnershipTypeLabel[OwnershipType.OWN] },
        { id: OwnershipType.LEASE, name: OwnershipTypeLabel[OwnershipType.LEASE] },
        { id: OwnershipType.RENT, name: OwnershipTypeLabel[OwnershipType.RENT] },
      ]}
    />
    <SelectInput alwaysOn label="Vendor" source="source" choices={VendorChoices} />
    <SelectInput
      alwaysOn
      label="Status"
      source="status"
      choices={[
        { id: EquipmentStatus.ACTIVE, name: EquipmentStatusLabel[EquipmentStatus.ACTIVE].label },
        { id: EquipmentStatus.INACTIVE, name: EquipmentStatusLabel[EquipmentStatus.INACTIVE].label },
        { id: EquipmentStatus.REMOVED, name: EquipmentStatusLabel[EquipmentStatus.REMOVED].label },
      ]}
    />
    <SelectInput
      alwaysOn
      label="Type"
      source="type"
      choices={[
        { id: TrailerType.DRY_VAN, name: TrailerTypeLabel[TrailerType.DRY_VAN] },
        { id: TrailerType.FLATBED, name: TrailerTypeLabel[TrailerType.FLATBED] },
        { id: TrailerType.REEFER, name: TrailerTypeLabel[TrailerType.REEFER] },
        { id: TrailerType.OTHER, name: TrailerTypeLabel[TrailerType.OTHER] },
      ]}
    />
    <BooleanInput label="Show only trailers with issues" source="alert" alwaysOn />
  </Filter>
)

const TrailerActions = ({ ...rest }: any) => {
  return (
    <TopToolbar {...sanitizeListRestProps(rest)}>
      <Button variant="text" href="/trailer-invoice-parser" size="small">
        Parse Trailer Invoices
      </Button>
    </TopToolbar>
  )
}

export const TrailersList = (props: any) => {
  const hasEquipmentManagementFlag = useHasEquipmentManagement()
  // TODO: @public-equipment-management Remove this
  if (!hasEquipmentManagementFlag) return null

  return (
    <CTList
      bulkActionButtons={false}
      filters={<TrailersListFilter />}
      title="Trailers"
      actions={<TrailerActions />}
      {...props}
    >
      <CTDatagrid optimized>
        <FunctionField
          label="Ownership"
          source="ownership_type"
          render={(record) => (
            <Text variant="paragraph1">{OwnershipTypeLabel[record?.ownership_type as OwnershipType]}</Text>
          )}
        />
        <FunctionField
          label="Number"
          source="vehicle_number"
          render={(record) => <Text variant="paragraph1">{record?.vehicle_number}</Text>}
        />
        <TextField label="Vendor" source="source" />
        <FunctionField
          label="Status"
          source="status"
          render={(record) => {
            const status = EquipmentStatusLabel[record?.status as EquipmentStatus]
            return (
              <Pill background={status.background} color={status.color}>
                {status.label}
              </Pill>
            )
          }}
        />
        <TextField label="Condition" source="condition" />
        <TextField label="Utilization" source="utilization" />
        <FunctionField
          label="Current driver"
          source="drivers"
          render={(record) => <Text variant="paragraph1">{record?.drivers?.[0].fullName}</Text>}
        />
        <FunctionField
          label="Type"
          source="type"
          render={(record) => (
            <Text variant="paragraph1">{TrailerTypeLabel[record?.type as TrailerType]}</Text>
          )}
        />
        <TextField label="Year" source="vehicleYear" />
        <TextField label="Expected location" source="expected_location" />
        <TextField label="Skybitz location" source="skybitz_location" />
        <FunctionField
          label="Alert"
          source="alert"
          render={(record) =>
            record?.alert && (
              <FlexBox gap="8px">
                <img src={ErrorIconFilled} alt="Error icon" />
                <Text mt={0.75} variant="paragraph1" fontColor={errorD1}>
                  {record.alert}
                </Text>
              </FlexBox>
            )
          }
        />
      </CTDatagrid>
    </CTList>
  )
}
